import { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { normalizeFullWidthNumerics } from '../../../utils/numericInputNormalize';
import { tppMutationError } from '../../../utils/tppApiError';
import {
  TPP_MODAL_CONTENT_CLASS,
  TppBtnSpinner,
  TppDateField,
  TppOptionalMark,
  TppRequiredMark,
  tppAmountStrForEdit,
} from './tppUiShared';

export type TppEventEditSeed = {
  id: number;
  eventKind: 'usage' | 'charge';
  /** 未返却の貸出中行（返却日は返却モーダル専用） */
  isOpenCheckout: boolean;
  purpose: string;
  place: string;
  route: string;
  amountNumber: number | null | undefined;
  outDate: string;
  inDate: string;
  departmentLabel: string;
  borrowerName: string;
};

export type TppEventEditSubmitPayload = {
  id: number;
  eventKind: 'usage' | 'charge';
  isOpenCheckout: boolean;
  purpose: string;
  place: string;
  route: string;
  amountStr: string;
  outDate: string;
  inDate: string;
  departmentLabel: string;
  borrowerName: string;
};

type FormState = TppEventEditSubmitPayload;

type TppEventEditModalProps = {
  closeLabel: string;
  open: boolean;
  onHide: () => void;
  seed: TppEventEditSeed | null;
  pending: boolean;
  error: unknown;
  onSubmit: (payload: TppEventEditSubmitPayload) => void;
};

function emptyForm(): FormState {
  return {
    id: 0,
    eventKind: 'usage',
    isOpenCheckout: false,
    purpose: '',
    place: '',
    route: '',
    amountStr: '',
    outDate: '',
    inDate: '',
    departmentLabel: '',
    borrowerName: '',
  };
}

/** 履歴行の編集（フォーム状態は内部保持） */
export default function TppEventEditModal({
  closeLabel,
  open,
  onHide,
  seed,
  pending,
  error,
  onSubmit,
}: TppEventEditModalProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(emptyForm);

  useEffect(() => {
    if (!open || !seed) return;
    setForm({
      id: seed.id,
      eventKind: seed.eventKind,
      isOpenCheckout: seed.isOpenCheckout,
      purpose: seed.purpose,
      place: seed.place,
      route: seed.route,
      amountStr: tppAmountStrForEdit(seed.eventKind, seed.amountNumber),
      outDate: seed.outDate,
      inDate: seed.isOpenCheckout ? '' : seed.inDate,
      departmentLabel: seed.departmentLabel,
      borrowerName: seed.borrowerName,
    });
  }, [open, seed]);

  const patch = (p: Partial<FormState>) => setForm((s) => ({ ...s, ...p }));
  const isCharge = form.eventKind === 'charge';
  const isOpenCheckout = form.isOpenCheckout;

  const title = isCharge
    ? t('tpp.edit_charge_title')
    : isOpenCheckout
      ? t('tpp.edit_checkout_title')
      : t('tpp.edit_event_title');

  return (
    <Modal
      show={open}
      onHide={onHide}
      centered
      size="lg"
      scrollable
      contentClassName={TPP_MODAL_CONTENT_CLASS}
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          if (!pending && form.id) {
            onSubmit({
              ...form,
              inDate: isOpenCheckout ? '' : form.inDate,
            });
          }
        }}
      >
        <Modal.Header closeButton closeLabel={closeLabel}>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isCharge ? (
            <>
              <TppDateField
                id="tpp-edit-charge-date"
                label={t('tpp.charge_date')}
                value={form.outDate}
                onChange={(outDate) => patch({ outDate })}
              />
              <Form.Group className="mb-2">
                <Form.Label htmlFor="tpp-edit-charge-amount">
                  {t('tpp.charge_amount')}
                  <TppRequiredMark />
                </Form.Label>
                <Form.Control
                  id="tpp-edit-charge-amount"
                  inputMode="decimal"
                  value={form.amountStr}
                  onChange={(e) =>
                    patch({ amountStr: normalizeFullWidthNumerics(e.target.value) })
                  }
                  className="tpp-amount-input"
                  autoFocus
                />
                <Form.Text muted>{t('tpp.edit_charge_amount_hint')}</Form.Text>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label htmlFor="tpp-edit-charge-note">
                  {t('tpp.charge_note')}
                  <TppOptionalMark />
                </Form.Label>
                <Form.Control
                  id="tpp-edit-charge-note"
                  value={form.purpose}
                  onChange={(e) => patch({ purpose: e.target.value })}
                />
              </Form.Group>
            </>
          ) : (
            <>
              {isOpenCheckout ? (
                <Alert variant="light" className="border small py-2 mb-3">
                  {t('tpp.edit_checkout_return_hint')}
                </Alert>
              ) : null}
              <Form.Group className="mb-2">
                <Form.Label htmlFor="tpp-edit-usage-purpose">{t('tpp.col_purpose')}</Form.Label>
                <Form.Control
                  id="tpp-edit-usage-purpose"
                  value={form.purpose}
                  onChange={(e) => patch({ purpose: e.target.value })}
                  autoFocus
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label htmlFor="tpp-edit-usage-place">{t('tpp.destination')}</Form.Label>
                <Form.Control
                  id="tpp-edit-usage-place"
                  value={form.place}
                  onChange={(e) => patch({ place: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label htmlFor="tpp-edit-usage-route">{t('tpp.route')}</Form.Label>
                <Form.Control
                  id="tpp-edit-usage-route"
                  as="textarea"
                  rows={2}
                  value={form.route}
                  onChange={(e) => patch({ route: e.target.value })}
                />
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label htmlFor="tpp-edit-usage-dept">
                      {t('tpp.department_label')}
                      <TppOptionalMark />
                    </Form.Label>
                    <Form.Control
                      id="tpp-edit-usage-dept"
                      value={form.departmentLabel}
                      onChange={(e) => patch({ departmentLabel: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label htmlFor="tpp-edit-usage-user">
                      {t('tpp.col_user')}
                      <TppOptionalMark />
                    </Form.Label>
                    <Form.Control
                      id="tpp-edit-usage-user"
                      value={form.borrowerName}
                      onChange={(e) => patch({ borrowerName: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={isOpenCheckout ? 12 : 6}>
                  <TppDateField
                    id="tpp-edit-usage-out"
                    label={t('tpp.col_out')}
                    value={form.outDate}
                    onChange={(outDate) => patch({ outDate })}
                  />
                </Col>
                {!isOpenCheckout ? (
                  <Col md={6}>
                    <TppDateField
                      id="tpp-edit-usage-in"
                      label={t('tpp.col_in')}
                      value={form.inDate}
                      onChange={(inDate) => patch({ inDate })}
                    />
                  </Col>
                ) : null}
              </Row>
              <Form.Group className="mb-2">
                <Form.Label htmlFor="tpp-edit-usage-amount">
                  {t('tpp.fare_amount')}
                </Form.Label>
                <Form.Control
                  id="tpp-edit-usage-amount"
                  inputMode="decimal"
                  value={form.amountStr}
                  onChange={(e) =>
                    patch({ amountStr: normalizeFullWidthNumerics(e.target.value) })
                  }
                  className="tpp-amount-input"
                />
                <Form.Text muted>
                  {isOpenCheckout
                    ? t('tpp.edit_checkout_amount_hint')
                    : t('tpp.edit_usage_amount_hint')}
                </Form.Text>
              </Form.Group>
            </>
          )}
          {!!error && <Alert variant="danger">{tppMutationError(error, t)}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={onHide}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={pending}>
            <TppBtnSpinner show={pending} />
            {t('common.save')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
